// @ts-nocheck
import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import classify from 'src/utils/classify';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import IndexStore from 'src/stores';
import {useHistory} from 'src/rerouter';
import {setExternalEventDetails} from 'src/action-creators/messages';
import {getCsrfToken} from 'src/action-creators/csrf';
import {selectCsrfToken} from 'src/selectors/csrf';
import {getReleaseFlags} from 'src/action-creators/product-flags';
import {getAgencyConfig} from 'src/action-creators/agency';
import {getDisplayConfig} from 'src/action-creators/display-config';
import {getAuthedUser, getAuthedAccount} from 'src/action-creators/accounts';
import {fetchAgentSettings} from 'src/action-creators/agent-settings';
import {fetchAllInboxes} from 'src/action-creators/chat/queues';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {ErrorMessage} from '@spaced-out/ui-design-system/lib/components/ErrorMessage';
import {selectAgencyData} from 'src/selectors/agency';

import {ButtonTextExtraSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import ProductBrandingContainer from './product-branding';
import {LanguageSupport} from 'src/components/settings/language-support';
import {
  CONSTANTS,
  getTranslatedText,
} from 'src/components/authentication/contants';
import * as reduxApi from 'src/utils/redux-api-v2';
import {useApi} from 'src/hooks/useApi';

import css from './container.css';
import maincss from '../main.css';


type AuthenticationContainerProps = {
  children: React.ReactNode;
  senseStore: IndexStore;
};
const AuthenticationContainer = (props: AuthenticationContainerProps) => {
  const {t} = useLabelTranslation();
  const {isExtension} = useEnvironmentContext();
  const router = useHistory();
  const dispatch = useDispatch();
  const {result, error} = useApi(`marketing-content`);
  const [showLoader, setShowLoader] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const {senseStore} = props;

  const ssoAuthUrl = useSelector(selectAgencyData).ssoAuthUrl;
  const showDualAuthForm = useSelector(selectAgencyData).showDualAuthForm;
  const languageSupportEnabled =
    useSelector(selectAgencyData).i18NEnableLanguageSupport;

  const handleExternalMessagingEvents = async (event: unknown) => {
    // TODO (Ashwini) need to check if this is needed. Causing re-renders because receiving events from react dev tools
    if (/^react-devtools/gi.test(event.data.source)) {
      return;
    }

    if (event.data.type === 'MULTIPLE_RECIPIENT') {
      setShowLoader(true);
      const {origin} = event;
      const {exchangeToken, phoneNumbers, channel} = event.data.payload;
      if (exchangeToken) {
        try {
          await dispatch(
            reduxApi.post('auth/exchange', {
              exchange_token: exchangeToken,
            }),
          );
        } catch (e: any) {
          setShowLoader(false);
          setShowError(true);
          throw e;
        }

        try {
          // authentication of user and also get the configurations
          const csrfToken = await dispatch(getCsrfToken());

          // setting the csrf token on window for api's called from src/utils/api-no-store.js
          if (window) {
            window.csrfToken = csrfToken;
          }

          await dispatch(getReleaseFlags());
          await dispatch(getAgencyConfig());
          await dispatch(getDisplayConfig());
          await dispatch(getAuthedUser(senseStore));
          await dispatch(fetchAgentSettings());
          await dispatch(getAuthedAccount(senseStore));

          // messaging
          await dispatch(fetchAllInboxes());
          await dispatch(
            setExternalEventDetails({phoneNumbers, channel, origin}),
          );
        } catch (e: any) {
          throw e;
        }

        router.replace(`/messages`);
        setShowLoader(false);
      }
    }
  };

  React.useEffect(() => {
    if (!isExtension) {
      window.addEventListener('message', handleExternalMessagingEvents);
    }

    return () => {
      if (!isExtension) {
        window.removeEventListener('message', handleExternalMessagingEvents);
      }
    };
  }, [showError, showLoader]);

  if (showLoader) {
    return (
      <div className={css.container}>
        <CircularLoader colorToken="colorFillPrimary" size="large" />
      </div>
    );
  }

  if (showError) {
    return (
      <div className={css.container}>
        <ErrorMessage
          classNames={{
            wrapper: css.errorMessage,
          }}
          description={t(
            'This_error_occurs_when_the_exchange_token_provided_for_authentication_is_invalid_or_expired_The_exchange_token_is_a_unique_identifier_generated_by_our_API_for_each_authentication_request_and_it_must_be_valid_and_within_its_timetolive_TTL_to_authenticate_successfully',
            'This error occurs when the exchange token provided for authentication is invalid or expired. The exchange token is a unique identifier generated by our API for each authentication request, and it must be valid and within its time-to-live (TTL) to authenticate successfully',
          )}
          heading={t('Invalid_Exchange_Token', 'Invalid Exchange Token')}
          imageVariant="forbidden"
          title={t(
            'Exchange_Token_Validation_Failure',
            'Exchange Token Validation Failure',
          )}
        />
      </div>
    );
  }

  return (
    <div className={css.mainContainer}>
      {!isExtension && (
        <ProductBrandingContainer
          content={
            result?.content === 'marketing' && !error ? result?.settings : null
          }
        />
      )}
      {!showError && !showLoader && (
        <div
          className={classify(css.content, {
            [css.contentExt]: isExtension,
            [css.contentBackground]: ssoAuthUrl && !showDualAuthForm,
          })}
        >
          {languageSupportEnabled && (
            <div
              className={classify(css.languageSupport, {
                [css.extLanguageSupport]: isExtension,
              })}
            >
              <LanguageSupport persistLanguage={false} />
            </div>
          )}
          <div className={css.chlidElement}>
            {React.cloneElement(props.children)}
          </div>
          <div
            className={classify(css.footerSection, {
              [css.footerSectionExt]: isExtension,
            })}
          >
            <a href={CONSTANTS.PRIVACY_POLICY_LINK} target="_blank">
              <ButtonTextExtraSmall
                className={classify(css.privaryPolicy, maincss.underline)}
              >
                {getTranslatedText(t, CONSTANTS.PRIVACY_POLICY_TEXT)}
              </ButtonTextExtraSmall>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthenticationContainer;
